import type {
	BundleInventoryItem,
	BundleVersionState,
} from "@internal/backstage-plugin-avd-common";
import { bundleVersionState } from "@internal/backstage-plugin-avd-common";
import { DESIRED_IAC_VERSIONS } from "@internal/backstage-plugin-deploy-common";
import type { ComponentType, ReactNode } from "react";

import type { HealthReason } from "./bundleHealth";

/** A status option the Filters popover offers; `id` is what the URL carries. */
export type StatusBucket = { id: string; label: string };

/**
 * What a bundle type adds to the inventory page. A type with no descriptor still lists, through
 * {@link genericDescriptor}; a descriptor only improves how its rows read.
 */
export type BundleInventoryDescriptor = {
	bundleType: string;
	/** Singular, lower case, for copy: "desktop". */
	noun: string;
	/** The status options this type offers, in display order. */
	buckets: (items: readonly BundleInventoryItem[]) => StatusBucket[];
	matchesBucket: (bucket: string, item: BundleInventoryItem) => boolean;
	/** A live-state mark (AVD: power), shown beside the bundle's health. */
	renderState?: (item: BundleInventoryItem) => ReactNode;
	/** Whether the type's own live read found the bundle drifted; `drifted` filters include it. */
	isEstateDrifted?: (item: BundleInventoryItem) => boolean;
	/** Whether the registry has a row to hold; a type with none for some rows (AVD: unregistered) says so. */
	hasRegistryRow?: (item: BundleInventoryItem) => boolean;
	/** The row has no bundle behind it (a torn-down one): no version, nothing to hold. */
	hasNoBundle?: (item: BundleInventoryItem) => boolean;
	/** What needs an operator's decision beyond the generic reasons (registry drift, expiring). */
	healthReasons?: (item: BundleInventoryItem) => HealthReason[];
	/** The run a bundle's last change came from, when it has one; admin-only, so a link to a private repo is fine. */
	runUrl?: (item: BundleInventoryItem) => string | undefined;
	/** Extra text the colleague search matches. */
	searchText?: (item: BundleInventoryItem) => string;
	/** The live state in words, for the detail sheet's summary sentence (AVD: "Off"). */
	stateText?: (item: BundleInventoryItem) => string | undefined;
	/** The type's own section of the detail sheet. */
	Details?: ComponentType<{ item: BundleInventoryItem; onChanged?: () => void }>;
};

const STATUS_PREFIX = "status:";

/** The descriptor of a type nothing registered: its own status strings are its buckets. */
export function genericDescriptor(bundleType: string): BundleInventoryDescriptor {
	return {
		bundleType,
		noun: "bundle",
		buckets: (items) =>
			[...new Set(items.map((item) => item.status))]
				.sort()
				.map((status) => ({ id: `${STATUS_PREFIX}${status}`, label: status })),
		matchesBucket: (bucket, item) => bucket === `${STATUS_PREFIX}${item.status}`,
	};
}

/** The registered descriptor of a type, or the generic one. */
export function descriptorFor(
	descriptors: readonly BundleInventoryDescriptor[],
	bundleType: string,
): BundleInventoryDescriptor {
	return descriptors.find((d) => d.bundleType === bundleType) ?? genericDescriptor(bundleType);
}

export type BundleIacVersion = { desired?: string; actual?: string; state: BundleVersionState };

/** Desired (this build's stamp for the type) against actual (the row's recorded version). */
export function bundleIacVersion(
	item: Pick<BundleInventoryItem, "bundleType" | "iacVersion" | "status">,
	descriptor?: Pick<BundleInventoryDescriptor, "hasNoBundle">,
): BundleIacVersion {
	const desired = DESIRED_IAC_VERSIONS[item.bundleType] || undefined;
	const actual = descriptor?.hasNoBundle?.(item as BundleInventoryItem)
		? undefined
		: item.iacVersion || undefined;
	return { desired, actual, state: bundleVersionState(desired, actual) };
}
